/**
 * Register a converted deck's webfonts at DOCUMENT level under NAMESPACED family names, and remap the
 * imported fragment to reference those namespaced names — so imported-slide fonts render correctly
 * without ever colliding with the presenter app's own fonts.
 *
 * WHY document level: an `html` slide is injected into a Shadow root, and a webfont registered by an
 * `@font-face` stylesheet that lives INSIDE a shadow root is ignored by Chromium for that shadow tree's
 * font matching — the sheet must live in the document. The converter's shared scaffold carries the
 * deck's `fonts.googleapis.com` `<link>`, so after `splitDeckHtml` it sits inside the fragment (i.e.
 * the shadow) and the text falls back to a metrically-different sans-serif and overlaps (Fredoka is
 * wide). So the font must be registered at document level.
 *
 * WHY namespaced: registering the deck's fonts under their real names (`Inter`, `Fredoka`, `Arimo`)
 * would put them in the SAME global font set the app uses to load a user's chosen presentation fonts
 * (see src/util.js / components-v2/font) — `Inter` in particular is in the app's font catalogue. To
 * make the imported deck's fonts UNABLE to restyle anything but imported-slide content, we register
 * them under a private `ahaimp-` prefix and rewrite only the imported fragment's own `font-family`
 * declarations to match. The app's global `Inter`/etc. are never touched, in either direction.
 *
 * SECURITY: document-level injection is a wider surface than the sanitised shadow, so it is strictly
 * ALLOWLISTED to Google Fonts hosts, FONTS ONLY. We fetch the allowlisted CSS (it sends
 * `access-control-allow-origin: *`), rewrite the `@font-face` family names to the namespaced ones and
 * inject that — we never copy an arbitrary fragment-sourced `<link>` or `<style>` into `<head>`. If the
 * fetch is unavailable the slide simply keeps today's fallback-font behaviour (no regression, and still
 * no global registration). The shadow sanitiser (./sanitize) is unchanged.
 */

// Google Fonts serves the CSS from fonts.googleapis.com and the font files from fonts.gstatic.com.
const ALLOWED_FONT_HOSTS = new Set(['fonts.googleapis.com', 'fonts.gstatic.com'])
// A prefix no app or system font uses, so a namespaced face can only be referenced by remapped slide
// content — never by app chrome (Plus Jakarta Sans) or a user's own presentation fonts.
const NS_PREFIX = 'ahaimp-'
// Marks the <style> elements WE injected, so repeated renders dedupe against our own by source href.
const HOISTED_MARKER = 'data-aha-deck-font'

const namespacedName = (family) => NS_PREFIX + family
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function isAllowedFontHref(href) {
  try {
    const url = new URL(href, window.location.href)
    return (url.protocol === 'https:' || url.protocol === 'http:') && ALLOWED_FONT_HOSTS.has(url.hostname)
  } catch (e) {
    return false
  }
}

// The font family names a Google Fonts href requests. Handles css v1 (`family=A:w|B:w`) and css2
// (repeated `family=A:...&family=B:...`); `+` is a space in a family name. `Inter:400,700` → `Inter`.
function fontFamiliesFromHref(href) {
  const families = []
  try {
    const url = new URL(href, window.location.href)
    url.searchParams.getAll('family').forEach((param) => {
      param.split('|').forEach((one) => {
        const name = one.split(':')[0].replace(/\+/g, ' ').trim()
        if (name && !families.includes(name)) families.push(name)
      })
    })
  } catch (e) {
    // malformed href → no families
  }
  return families
}

// Rewrite only the `font-family:` declaration VALUES in the fragment, remapping each deck family to its
// namespaced name (quoted or bare token). Scoped to font-family values so it can never touch visible
// text content; leaves system fonts (Arial, Helvetica, sans-serif) and every other family untouched.
function remapFontFamilies(html, families) {
  if (!html || !families.length) return html
  // The value runs until the CSS declaration ends (`;`/`}`) OR — for the LAST declaration in an inline
  // `style="…"` with no trailing `;` — the attribute-closing quote. A bare `[^;}]+` over-captures past
  // that quote into the rest of the document, so an unquoted trailing family is never namespaced (it
  // ends up followed by `"`, which the bare-token remap below won't match) and the slide falls back.
  // Consume whole quoted family names, otherwise any char that can't be a value terminator (`"'<>`).
  return html.replace(/font-family\s*:\s*((?:"[^"]*"|'[^']*'|[^;}"'<>])*)/gi, (_whole, value) => {
    let out = value
    families.forEach((family) => {
      const ns = namespacedName(family)
      const esc = escapeRegExp(family)
      out = out.replace(new RegExp(`(['"])${esc}\\1`, 'g'), `$1${ns}$1`) // 'Inter' / "Inter"
      out = out.replace(new RegExp(`(^|[,\\s])${esc}(?=\\s*(,|$))`, 'g'), `$1${ns}`) // bare Inter token
    })
    return `font-family:${out}`
  })
}

// Fetch an allowlisted Google Fonts sheet and register its faces at document level under the namespaced
// family names. Deduped by href across renders; fire-and-forget (the font swaps in when it loads).
function registerNamespacedFontSheet(href, families) {
  const marked = document.head.querySelectorAll(`style[${HOISTED_MARKER}]`)
  for (let i = 0; i < marked.length; i += 1) {
    if (marked[i].getAttribute('data-src') === href) return
  }
  // Reserve the marker synchronously so two concurrent renders don't both fetch/inject.
  const styleEl = document.createElement('style')
  styleEl.setAttribute(HOISTED_MARKER, '')
  styleEl.setAttribute('data-src', href)
  document.head.appendChild(styleEl)

  if (typeof fetch !== 'function') return // no fetch → keep today's fallback-font behaviour
  fetch(href)
    .then((res) => {
      if (!res.ok) throw new Error(`font css ${res.status}`)
      return res.text()
    })
    .then((css) => {
      let out = css
      families.forEach((family) => {
        const esc = escapeRegExp(family)
        // @font-face `font-family: 'Inter'` → `font-family:'ahaimp-Inter'` (only the deck's families).
        out = out.replace(new RegExp(`font-family:\\s*(['"]?)${esc}\\1`, 'gi'), `font-family:'${namespacedName(family)}'`)
      })
      styleEl.textContent = out
      return out
    })
    .catch(() => {
      // Degrade to today's fallback-font behaviour rather than register a GLOBAL name — never introduce
      // the collision the namespacing exists to prevent. The empty marker keeps the dedupe stable.
    })
}

/**
 * Prepare a slide/deck HTML fragment for shadow injection: register its allowlisted webfonts at
 * document level under namespaced names, and return the fragment remapped to reference those names.
 * Idempotent and safe to call on every render. Outside a browser (or with no font link) returns the
 * html unchanged.
 * @param {string} html a sanitised slide fragment (its shared scaffold carries the deck's font <link>)
 * @returns {string} the fragment with its deck `font-family` references namespaced
 */
export function namespaceDeckFonts(html) {
  if (!html || typeof document === 'undefined') return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const families = []
  doc.querySelectorAll('link[rel~="stylesheet"][href]').forEach((link) => {
    const href = link.getAttribute('href')
    if (!isAllowedFontHref(href)) return
    const linkFamilies = fontFamiliesFromHref(href)
    if (!linkFamilies.length) return
    registerNamespacedFontSheet(href, linkFamilies)
    linkFamilies.forEach((f) => { if (!families.includes(f)) families.push(f) })
  })
  return remapFontFamilies(html, families)
}
